import clsx from 'clsx';
import {
  Button,
  DatePicker,
  Dropdown,
  ErrorContainer,
  InputField,
  LabelComponent,
  Stack,
  StandardCheckbox
} from 'components';
import AccordionComponent from 'components/accordion/accordion';
import { useFormikContext } from 'formik';
import { ComponentProps, useEffect } from 'react';
import { getInsurancesAutocomplete } from 'store/clients/clientsActions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useResponsive } from 'utils/hooks/useMediaQuery';

import { FormValues, Insurance as InsuranceType } from '../../newClientForm';
import SectionTitle from '../sectionTitle/sectionTitle';
import style from './insurance.module.scss';

const secondaryInsuranceInitialValues = {
  insuredSameAsClient: true,
  insuranceCategory: '',
  otherInsuranceCategory: '',
  insurancePlan: null,
  otherInsurancePlan: '',
  insuranceNumber: '',
  groupNumber: '',
  relationshipToClient: '',
  insuredName: '',
  insuredDateOfBirth: '',
  insuredStreetAddress: '',
  insuredStreetAddress2: '',
  insuredCity: '',
  insuredState: '',
  insuredZipCode: ''
} satisfies InsuranceType;

const Wrapper = ({
  children,
  ...props
}: { children: React.ReactNode } & ComponentProps<typeof Stack>) => {
  const { isTablet } = useResponsive();

  return (
    <Stack
      width="100%"
      display="flex"
      flexDirection={isTablet ? 'column' : 'row'}
      gap={isTablet ? '4px' : '24px'}
      {...props}
    >
      {children}
    </Stack>
  );
};

const InsuredNotSameAsClientForm = ({
  insuranceType
}: {
  insuranceType: 'primaryInsurance' | 'secondaryInsurance';
}) => {
  const { isTablet } = useResponsive();
  const formik = useFormikContext<FormValues>();

  const touched = formik.touched[insuranceType];
  const errors = formik.errors[insuranceType];
  if (typeof touched === 'boolean') {
    return null;
  }

  if (typeof errors === 'string') {
    return null;
  }

  return (
    <Stack width="100%">
      <Wrapper alignItems={isTablet ? undefined : 'flex-end'}>
        <Stack flex={3}>
          <LabelComponent text="Insured's Relationship to Client" />
          <Dropdown
            name={`${insuranceType}.relationshipToClient`}
            value={formik.values[insuranceType]?.relationshipToClient ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Select"
            data={[]}
          />
          <ErrorContainer
            visible={touched?.relationshipToClient && errors?.relationshipToClient}
            errors={formik.errors}
            name={`${insuranceType}.relationshipToClient`}
          />
        </Stack>
        <Stack flex={3}>
          <LabelComponent text="Insured's Name" />
          <InputField
            name={`${insuranceType}.insuredName`}
            value={formik.values[insuranceType]?.insuredName ?? ''}
            onChange={formik.handleChange}
            placeholder="Enter"
            onBlur={formik.handleBlur}
          />
          <ErrorContainer
            visible={touched?.insuredName && errors?.insuredName}
            errors={formik.errors}
            name={`${insuranceType}.insuredName`}
          />
        </Stack>
        <Stack flex={3}>
          <LabelComponent text="Insured's DOB" />
          <DatePicker
            value={formik.values[insuranceType]?.insuredDateOfBirth ?? ''}
            onChange={(value: string) => {
              formik.setFieldValue(`${insuranceType}.insuredDateOfBirth`, value).then(() => {
                formik.setFieldTouched(`${insuranceType}.insuredDateOfBirth`, true);
              });
            }}
            onBlur={() => formik.setFieldTouched(`${insuranceType}.insuredDateOfBirth`, true)}
          />
          <ErrorContainer
            visible={touched?.insuredDateOfBirth && errors?.insuredDateOfBirth}
            errors={formik.errors}
            name={`${insuranceType}.insuredDateOfBirth`}
          />
        </Stack>
      </Wrapper>
      <Wrapper>
        <Stack flex={2}>
          <LabelComponent text="Insured's Street Address or Location" />
          <InputField
            name={`${insuranceType}.insuredStreetAddress`}
            value={formik.values[insuranceType]?.insuredStreetAddress ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter"
          />
          <ErrorContainer
            visible={touched?.insuredStreetAddress && errors?.insuredStreetAddress}
            errors={formik.errors}
            name={`${insuranceType}.insuredStreetAddress`}
          />
        </Stack>
        <Stack flex={2}>
          <LabelComponent text="Insured's Street Address Line 2" />
          <InputField
            name={`${insuranceType}.insuredStreetAddress2`}
            value={formik.values[insuranceType]?.insuredStreetAddress2 ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter"
          />
          <ErrorContainer
            visible={touched?.insuredStreetAddress2 && errors?.insuredStreetAddress2}
            errors={formik.errors}
            name={`${insuranceType}.insuredStreetAddress2`}
          />
        </Stack>
      </Wrapper>
      <Wrapper>
        <Stack flex={3}>
          <LabelComponent text="Insured's City" />
          <InputField
            name={`${insuranceType}.insuredCity`}
            value={formik.values[insuranceType]?.insuredCity ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter"
          />
          <ErrorContainer
            visible={touched?.insuredCity && errors?.insuredCity}
            errors={formik.errors}
            name={`${insuranceType}.insuredCity`}
          />
        </Stack>
        <Stack flex={3}>
          <LabelComponent text="Insured's State" />
          <InputField
            name={`${insuranceType}.insuredState`}
            value={formik.values[insuranceType]?.insuredState ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter"
          />
          <ErrorContainer
            visible={touched?.insuredState && errors?.insuredState}
            errors={formik.errors}
            name={`${insuranceType}.insuredState`}
          />
        </Stack>
        <Stack flex={3}>
          <LabelComponent text="Insured's Zip Code" />
          <InputField
            name={`${insuranceType}.insuredZipCode`}
            value={formik.values[insuranceType]?.insuredZipCode ?? ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Enter"
          />
          <ErrorContainer
            visible={touched?.insuredZipCode && errors?.insuredZipCode}
            errors={formik.errors}
            name={`${insuranceType}.insuredZipCode`}
          />
        </Stack>
      </Wrapper>
    </Stack>
  );
};

const InsuranceForm = ({
  insuranceType
}: {
  insuranceType: 'primaryInsurance' | 'secondaryInsurance';
}) => {
  const insuranceCategories = useAppSelector((state) => state.clients.insurancesAutocomplete.data);
  const formik = useFormikContext<FormValues>();

  const touched = formik.touched[insuranceType];
  const errors = formik.errors[insuranceType];

  if (typeof touched === 'boolean') {
    return null;
  }

  if (typeof errors === 'string') {
    return null;
  }

  return (
    <Stack>
      <Stack mb="10px">
        <Stack mb="16px">
          <SectionTitle
            action={
              insuranceType === 'secondaryInsurance' && (
                <button onClick={() => formik.setFieldValue('secondaryInsurance', null)}>
                  <span className={clsx('material-icons', style.deleteIcon)}>delete</span>
                </button>
              )
            }
          >
            {insuranceType === 'primaryInsurance' ? 'Primary Insurance' : 'Secondary Insurance'}
          </SectionTitle>
        </Stack>
        <StandardCheckbox
          label="Insured is the same as client"
          value={formik.values[insuranceType]?.insuredSameAsClient}
          onChange={(e) =>
            formik.setFieldValue(`${insuranceType}.insuredSameAsClient`, e.target.checked)
          }
          name={`${insuranceType}.insuredSameAsClient`}
          onBlur={formik.handleBlur}
        />
        <ErrorContainer
          visible={touched?.insurancePlan && errors?.insuredSameAsClient}
          errors={formik.errors}
          name={`${insuranceType}.insuredSameAsClient`}
        />
      </Stack>
      {!formik.values[insuranceType]?.insuredSameAsClient && (
        <>
          <InsuredNotSameAsClientForm insuranceType={insuranceType} />
          <Stack className={style.line} mb="16px" />
        </>
      )}
      <Wrapper>
        <Stack flex={1}>
          <Stack>
            <LabelComponent text="Insurance Category" />
            <Dropdown
              name={`${insuranceType}.insuranceCategory`}
              value={formik.values[insuranceType]?.insuranceCategory ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Select"
              data={insuranceCategories ?? []}
            />
            <ErrorContainer
              visible={touched?.insuranceCategory && errors?.insuranceCategory}
              errors={formik.errors}
              name={`${insuranceType}.insuranceCategory`}
            />
          </Stack>
          {formik.values[insuranceType]?.insuranceCategory === 'OTHER' && (
            <Stack>
              <LabelComponent text="Other Insurance Category" />
              <InputField
                name={`${insuranceType}.otherInsuranceCategory`}
                value={formik.values[insuranceType]?.otherInsuranceCategory ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Stack>
          )}
        </Stack>
        <Stack flex={1}>
          <Stack>
            <LabelComponent text="Insurance Plan" />
            {/* Replace this with a dropdown similar to ZCodes*/}
            <Dropdown
              name={`${insuranceType}.insurancePlan`}
              // @ts-expect-error this will be replaced
              value={formik.values[insuranceType]?.insurancePlan ?? ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Enter"
              data={[]}
            />
            <ErrorContainer
              visible={touched?.insurancePlan && errors?.insurancePlan}
              errors={formik.errors}
              name={`${insuranceType}.insurancePlan`}
            />
          </Stack>
          {formik.values[insuranceType]?.insurancePlan?.name === 'Other' && (
            <Stack>
              <LabelComponent text="Other Insurance Plan" />
              <InputField
                name={`${insuranceType}.otherInsurancePlan`}
                value={formik.values[insuranceType]?.otherInsurancePlan ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Stack>
          )}
        </Stack>
      </Wrapper>
      <Wrapper>
        <Stack flex={1}>
          <Stack>
            <LabelComponent text="Client's Member ID Number" />
            <InputField
              name={`${insuranceType}.insuranceNumber`}
              value={formik.values[insuranceType]?.insuranceNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter"
            />
            <ErrorContainer
              visible={touched?.insuranceNumber && errors?.insuranceNumber}
              errors={formik.errors}
              name={`${insuranceType}.insuranceNumber`}
            />
          </Stack>
        </Stack>
        <Stack flex={1}>
          <Stack>
            <LabelComponent text="Group Number" />
            <InputField
              name={`${insuranceType}.groupNumber`}
              value={formik.values[insuranceType]?.groupNumber}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Enter"
            />
            <ErrorContainer
              visible={touched?.groupNumber && errors?.groupNumber}
              errors={formik.errors}
              name={`${insuranceType}.groupNumber`}
            />
          </Stack>
        </Stack>
      </Wrapper>
      {insuranceType === 'primaryInsurance' && !formik.values.secondaryInsurance && (
        <Stack width="160px" mt="10px">
          <Button
            icon={<span className="material-icons">add</span>}
            theme="transparent"
            type="button"
            text="Add another"
            handleOnClick={() =>
              formik.setFieldValue('secondaryInsurance', secondaryInsuranceInitialValues)
            }
            size="small"
          />
        </Stack>
      )}
      <Stack
        className={style.line}
        mb={insuranceType === 'secondaryInsurance' ? '24px' : '32px'}
        mt={
          insuranceType === 'primaryInsurance' && !formik.values.secondaryInsurance
            ? '24px'
            : '12px'
        }
      />
    </Stack>
  );
};

const Insurance = () => {
  const formik = useFormikContext<FormValues>();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getInsurancesAutocomplete());
  }, []);

  return (
    <AccordionComponent accordionTabId="insurance" title="Insurance">
      <Stack mt="24px">
        <InsuranceForm insuranceType="primaryInsurance" />
        {formik.values.secondaryInsurance && <InsuranceForm insuranceType="secondaryInsurance" />}
        <Stack display="flex" flexDirection="row" gap="24px" width="100%">
          <Stack width="50%">
            <Stack>
              <LabelComponent text="Insurance Notes" />
              <InputField
                name="primaryInsurance.insuranceNote"
                value={formik.values?.insuranceNote}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter"
              />
              <ErrorContainer
                visible={formik.touched?.insuranceNote && formik.errors?.insuranceNote}
                errors={formik.errors}
                name="insuranceNote"
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </AccordionComponent>
  );
};

export default Insurance;
